import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSignUp } from '@clerk/clerk-react';
import { Loader2, AlertCircle, Eye, EyeOff, ExternalLink } from 'lucide-react';
import api from '../services/api';

export default function SetupTokenScreen() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { signUp, setActive, isLoaded } = useSignUp();

  // State machine: LOADING | INVALID | READY | SIGNING_UP | SMS_PENDING | VERIFYING | EMAIL_PENDING | VERIFYING_EMAIL | DONE
  const [phase, setPhase] = useState('LOADING');
  const [invitationData, setInvitationData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // Form state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // SMS state
  const [smsCode, setSmsCode] = useState(['', '', '', '', '', '']);
  const [smsError, setSmsError] = useState(null);
  const [smsExpired, setSmsExpired] = useState(false);
  const smsRefs = useRef([]);

  // Email verification state
  const [emailCode, setEmailCode] = useState(['', '', '', '', '', '']);
  const [emailError, setEmailError] = useState(null);
  const emailRefs = useRef([]);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setPhase('INVALID');
      setErrorMsg('No invitation token provided.');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invitations/${token}`);
        const data = await res.json();
        if (res.status === 200) {
          setInvitationData(data);
          setPhase('READY');
        } else {
          setPhase('INVALID');
          setErrorCode(data.error);
          setErrorMsg(data.message || 'This invitation link is invalid.');
        }
      } catch (err) {
        setPhase('INVALID');
        setErrorMsg('Could not validate this invitation. Check your connection and try again.');
      }
    })();
  }, [token]);

  // Focus first SMS input when overlay appears
  useEffect(() => {
    if (phase === 'SMS_PENDING') {
      setTimeout(() => smsRefs.current[0]?.focus(), 100);
    }
  }, [phase]);

  const handleCreateAccount = async () => {
    if (!isLoaded) return;
    if (!password || password.length < 8) { setErrorMsg('Password must be at least 8 characters.'); return; }
    if (!mobile) { setErrorMsg('Mobile number is required.'); return; }
    if (!privacyAccepted) { setErrorMsg('You must accept the privacy notice.'); return; }

    setPhase('SIGNING_UP');
    setErrorMsg(null);

    try {
      await signUp.create({
        emailAddress: invitationData.email,
        password,
        phoneNumber: mobile,
      });
      await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
      setPhase('SMS_PENDING');
    } catch (err) {
      const e0 = err.errors?.[0];
      setErrorMsg(e0?.longMessage || e0?.message || 'Could not create account. Please try again.');
      setPhase('READY');
    }
  };

  const handleSmsDigit = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...smsCode];
    next[index] = digit;
    setSmsCode(next);
    setSmsError(null);
    if (digit && index < 5) smsRefs.current[index + 1]?.focus();
    if (digit && index === 5) {
      const full = next.join('');
      if (full.length === 6) handleVerifySms(full);
    }
  };

  const handleSmsKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !smsCode[index] && index > 0) {
      smsRefs.current[index - 1]?.focus();
    }
  };

  const handleSmsPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const next = [...smsCode];
      for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
      setSmsCode(next);
      smsRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (pasted.length === 6) handleVerifySms(pasted);
    }
  };

  const handleVerifySms = async (codeString) => {
    const fullCode = codeString || smsCode.join('');
    if (fullCode.length !== 6) { setSmsError('Please enter all 6 digits'); return; }
    if (!isLoaded) return;

    setPhase('VERIFYING');
    setSmsError(null);

    try {
      const result = await signUp.attemptPhoneNumberVerification({ code: fullCode });

      // After phone verification, Clerk returns missing_requirements if email
      // is still unverified — that is the expected state. Check whether we can
      // proceed to email verification or if something unexpected is missing.
      if (result.status === 'missing_requirements') {
        const unverified = signUp.unverifiedFields || [];
        const missing = signUp.missingFields || [];
        console.warn('[SETUP] phone verified, checking remaining requirements:', {
          status: result.status, missingFields: missing, unverifiedFields: unverified,
        });

        // If only email_address is unverified and nothing is missing, proceed
        if (missing.length === 0 && unverified.length === 1 && unverified[0] === 'email_address') {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          setEmailCode(['', '', '', '', '', '']);
          setEmailError(null);
          setPhase('EMAIL_PENDING');
          return;
        }

        // Something unexpected is still required — surface friendly error
        setSmsError('We could not complete your sign-up. Please try again, or contact your administrator if this continues.');
        setPhase('SMS_PENDING');
        return;
      }

      if (result.status !== 'complete') {
        setSmsError('We could not complete your sign-up. Please try again, or contact your administrator if this continues.');
        setPhase('SMS_PENDING');
        return;
      }

      // If phone verification somehow completes the whole signup (no email
      // verification required), go straight to completion.
      await setActive({ session: result.createdSessionId });
      await api.post(`/invitations/${token}/complete`, { privacy_notice_accepted: true });
      setPhase('DONE');
      navigate('/setup/install');
    } catch (err) {
      const e0 = err.errors?.[0];
      const msg = e0?.longMessage || e0?.message || err.message || '';
      if (msg.toLowerCase().includes('expired')) {
        setSmsExpired(true);
        setSmsError(null);
      } else {
        setSmsError(msg || 'Invalid code. Please try again.');
        setSmsCode(['', '', '', '', '', '']);
        smsRefs.current[0]?.focus();
      }
      setPhase('SMS_PENDING');
    }
  };

  const handleResendSms = async () => {
    setSmsExpired(false);
    setSmsCode(['', '', '', '', '', '']);
    setSmsError(null);
    try {
      await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
      smsRefs.current[0]?.focus();
    } catch (err) {
      setSmsError('Could not send a new code. Please try again.');
    }
  };

  const handleEmailDigit = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...emailCode];
    next[index] = digit;
    setEmailCode(next);
    setEmailError(null);
    if (digit && index < 5) emailRefs.current[index + 1]?.focus();
    if (digit && index === 5) {
      const full = next.join('');
      if (full.length === 6) handleVerifyEmail(full);
    }
  };

  const handleEmailKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !emailCode[index] && index > 0) {
      emailRefs.current[index - 1]?.focus();
    }
  };

  const handleEmailPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const next = [...emailCode];
      for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
      setEmailCode(next);
      emailRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (pasted.length === 6) handleVerifyEmail(pasted);
    }
  };

  const handleVerifyEmail = async (codeString) => {
    const fullCode = codeString || emailCode.join('');
    if (fullCode.length !== 6) { setEmailError('Please enter all 6 digits'); return; }
    if (!isLoaded) return;

    setPhase('VERIFYING_EMAIL');
    setEmailError(null);

    try {
      const result = await signUp.attemptEmailAddressVerification({ code: fullCode });
      if (result.status !== 'complete') {
        const missing = signUp.missingFields || [];
        const unverified = signUp.unverifiedFields || [];
        console.warn('[SETUP] signUp still not complete after email:', {
          status: result.status, missingFields: missing, unverifiedFields: unverified,
        });
        setEmailError('We could not complete your sign-up. Please try again, or contact your administrator if this continues.');
        setPhase('EMAIL_PENDING');
        return;
      }
      await setActive({ session: result.createdSessionId });
      await api.post(`/invitations/${token}/complete`, { privacy_notice_accepted: true });
      setPhase('DONE');
      navigate('/setup/install');
    } catch (err) {
      const e0 = err.errors?.[0];
      const msg = e0?.longMessage || e0?.message || err.message || '';
      setEmailError(msg || 'Invalid code. Please try again.');
      setEmailCode(['', '', '', '', '', '']);
      emailRefs.current[0]?.focus();
      setPhase('EMAIL_PENDING');
    }
  };

  // LOADING state
  if (phase === 'LOADING') {
    return (
      <div style={styles.screen}>
        <Loader2 size={32} color="white" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  // INVALID TOKEN state — show error, no form
  if (phase === 'INVALID') {
    return (
      <div style={styles.screen}>
        <Logo />
        <div style={styles.card}>
          <div style={styles.cardTitle}>Invitation Unavailable</div>
          <div style={{ background: '#fff5f5', border: '1.5px solid #fecaca', borderLeft: '4px solid #dc2626', borderRadius: '10px', padding: '14px 16px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '13px', color: '#dc2626', lineHeight: 1.5 }}>{errorMsg}</div>
          </div>
          <p style={{ fontSize: '13px', color: '#6b7a94', marginTop: '20px', lineHeight: 1.6 }}>
            Contact your administrator for a new invitation, or sign in if you already have an account.
          </p>
          <button onClick={() => navigate('/login')} style={styles.linkButton}>Sign in</button>
        </div>
      </div>
    );
  }

  // READY / SIGNING_UP / SMS_PENDING / VERIFYING — main form + optional SMS overlay
  return (
    <div style={styles.screen}>
      <Logo />

      <div style={styles.card}>
        <div style={styles.welcomeTitle}>Welcome, {invitationData.officer_name || 'Officer'}</div>
        <p style={{ fontSize: '14px', color: '#001a4d', marginBottom: '14px', lineHeight: 1.6 }}>
          Your employer <strong>{invitationData.company_name || 'your employer'}</strong> has invited you to the Vehicle Crime Intelligence Network (VCIN).
        </p>
        <p style={{ fontSize: '13px', color: '#4a5a7a', marginBottom: '20px', lineHeight: 1.6 }}>
          VCIN helps you check vehicle registration numbers against a national database of suspicious vehicles. Your account is private to you. Your employer cannot read what you submit. Data is held by Digital Software Group Ltd in the UK and protected under GDPR.
          {' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#001a4d', fontWeight: 600, textDecoration: 'underline' }}>
            Read the full privacy notice <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
          </a>
        </p>

        {/* Email — display only */}
        <div style={styles.fieldLabel}>YOUR EMAIL</div>
        <div style={styles.fieldDisplay}>{invitationData.email}</div>

        {/* Password */}
        <div style={styles.fieldLabel}>CHOOSE A PASSWORD</div>
        <div style={{ position: 'relative', marginBottom: '14px' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={phase !== 'READY'}
            placeholder="At least 8 characters"
            style={styles.fieldInput}
            autoComplete="new-password"
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeButton} aria-label="Toggle password visibility">
            {showPassword ? <EyeOff size={18} color="#6b7a94" /> : <Eye size={18} color="#6b7a94" />}
          </button>
        </div>

        {/* Mobile */}
        <div style={styles.fieldLabel}>MOBILE NUMBER</div>
        <input
          type="tel"
          inputMode="tel"
          value={mobile}
          onChange={e => setMobile(e.target.value)}
          disabled={phase !== 'READY'}
          placeholder="e.g. +44 7700 900123"
          style={styles.fieldInput}
          autoComplete="tel"
        />

        {/* Privacy checkbox */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '18px', marginBottom: '20px', cursor: phase === 'READY' ? 'pointer' : 'default' }}>
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={e => setPrivacyAccepted(e.target.checked)}
            disabled={phase !== 'READY'}
            style={{ marginTop: '3px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '13px', color: '#001a4d', lineHeight: 1.5 }}>
            I have read and accepted the <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#001a4d', textDecoration: 'underline' }}>privacy notice</a>.
          </span>
        </label>

        {/* Form-level error */}
        {errorMsg && phase === 'READY' && (
          <div style={styles.errorBox}>
            <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={styles.errorText}>{errorMsg}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleCreateAccount}
          disabled={phase !== 'READY' || !password || !mobile || !privacyAccepted}
          style={{
            ...styles.submitButton,
            background: (phase === 'READY' && password && mobile && privacyAccepted) ? '#001a4d' : '#c8d0e0',
            color: (phase === 'READY' && password && mobile && privacyAccepted) ? 'white' : '#8a9ab8',
            cursor: (phase === 'READY' && password && mobile && privacyAccepted) ? 'pointer' : 'not-allowed',
          }}
        >
          {phase === 'SIGNING_UP' ? (
            <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</>
          ) : 'Create my account'}
        </button>
      </div>

      {/* SMS Overlay */}
      {(phase === 'SMS_PENDING' || phase === 'VERIFYING') && (
        <div style={styles.smsBackdrop}>
          <div style={styles.smsCard}>
            <div style={styles.cardTitle}>Verify your mobile</div>
            <p style={{ fontSize: '13px', color: '#7a8aaa', lineHeight: 1.6, marginBottom: '20px' }}>
              We sent a 6-digit code by SMS to the number you provided. Enter it below.
            </p>

            {smsError && (
              <div style={styles.errorBox}>
                <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span style={styles.errorText}>{smsError}</span>
              </div>
            )}

            {smsExpired && (
              <div style={{ background: '#fffbf0', border: '1px solid #f0d890', borderLeft: '3px solid #C9973A', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#92400E', fontWeight: 600, marginBottom: '6px' }}>Code expired</p>
                <button onClick={handleResendSms} style={{ background: 'none', border: 'none', padding: 0, fontSize: '13px', color: '#001a4d', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                  Send a new code
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }} onPaste={handleSmsPaste}>
              {smsCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => smsRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleSmsDigit(index, e.target.value)}
                  onKeyDown={e => handleSmsKeyDown(index, e)}
                  disabled={phase === 'VERIFYING'}
                  style={{
                    width: '44px', height: '52px', textAlign: 'center',
                    fontSize: '22px', fontWeight: 700, color: '#001a4d',
                    fontFamily: "'Oswald', sans-serif",
                    background: digit ? '#f0f4ff' : '#f7f8fc',
                    border: `2px solid ${digit ? '#001a4d' : '#e4e8f0'}`,
                    borderRadius: '10px', outline: 'none',
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => handleVerifySms()}
              disabled={phase === 'VERIFYING' || smsCode.some(d => !d)}
              style={{
                ...styles.submitButton,
                background: (!smsCode.some(d => !d) && phase !== 'VERIFYING') ? '#001a4d' : '#c8d0e0',
                color: (!smsCode.some(d => !d) && phase !== 'VERIFYING') ? 'white' : '#8a9ab8',
                cursor: (!smsCode.some(d => !d) && phase !== 'VERIFYING') ? 'pointer' : 'not-allowed',
              }}
            >
              {phase === 'VERIFYING' ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Verifying...</>
              ) : 'Verify code'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '11px', color: '#b0bcd4', marginTop: '14px' }}>
              Code expires in 5 minutes
            </p>
          </div>
        </div>
      )}

      {/* Email verification overlay */}
      {(phase === 'EMAIL_PENDING' || phase === 'VERIFYING_EMAIL') && (
        <div style={styles.smsBackdrop}>
          <div style={styles.smsCard}>
            <div style={styles.cardTitle}>Verify your email</div>
            <p style={{ fontSize: '13px', color: '#7a8aaa', lineHeight: 1.6, marginBottom: '20px' }}>
              We sent a 6-digit code to {invitationData?.email}. Enter it below. Check your spam folder if it does not arrive.
            </p>

            {emailError && (
              <div style={styles.errorBox}>
                <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span style={styles.errorText}>{emailError}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }} onPaste={handleEmailPaste}>
              {emailCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => emailRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleEmailDigit(index, e.target.value)}
                  onKeyDown={e => handleEmailKeyDown(index, e)}
                  disabled={phase === 'VERIFYING_EMAIL'}
                  style={{
                    width: '44px', height: '52px', textAlign: 'center',
                    fontSize: '22px', fontWeight: 700, color: '#001a4d',
                    fontFamily: "'Oswald', sans-serif",
                    background: digit ? '#f0f4ff' : '#f7f8fc',
                    border: `2px solid ${digit ? '#001a4d' : '#e4e8f0'}`,
                    borderRadius: '10px', outline: 'none',
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => handleVerifyEmail()}
              disabled={phase === 'VERIFYING_EMAIL' || emailCode.some(d => !d)}
              style={{
                ...styles.submitButton,
                background: (!emailCode.some(d => !d) && phase !== 'VERIFYING_EMAIL') ? '#001a4d' : '#c8d0e0',
                color: (!emailCode.some(d => !d) && phase !== 'VERIFYING_EMAIL') ? 'white' : '#8a9ab8',
                cursor: (!emailCode.some(d => !d) && phase !== 'VERIFYING_EMAIL') ? 'pointer' : 'not-allowed',
              }}
            >
              {phase === 'VERIFYING_EMAIL' ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Verifying...</>
              ) : 'Verify email'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      <div style={{
        background: '#F5C400', border: '3px solid #111',
        borderRadius: '8px', display: 'inline-block',
        padding: '10px 28px', marginBottom: '16px',
      }}>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '36px',
          fontWeight: 700, color: '#111', letterSpacing: '8px',
        }}>VCIN</div>
      </div>
      <div style={{
        fontFamily: "'Oswald', sans-serif", fontSize: '11px',
        fontWeight: 600, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
      }}>Vehicle Crime Intelligence Network</div>
    </div>
  );
}

const styles = {
  screen: {
    minHeight: '100vh', background: '#001a4d',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px', fontFamily: "'Inter', sans-serif",
  },
  card: {
    background: 'white', borderRadius: '16px', padding: '28px 24px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  smsBackdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 26, 77, 0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px', zIndex: 100,
  },
  smsCard: {
    background: 'white', borderRadius: '16px', padding: '28px 24px',
    width: '100%', maxWidth: '380px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  cardTitle: {
    fontFamily: "'Oswald', sans-serif", fontSize: '18px',
    fontWeight: 700, color: '#001a4d', letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '6px',
  },
  welcomeTitle: {
    fontFamily: "'Oswald', sans-serif", fontSize: '20px',
    fontWeight: 700, color: '#001a4d', letterSpacing: '1px',
    marginBottom: '12px',
  },
  fieldLabel: {
    fontFamily: "'Oswald', sans-serif", fontSize: '10px',
    fontWeight: 600, color: '#6b7a94', letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '6px', marginTop: '14px',
  },
  fieldDisplay: {
    fontSize: '14px', color: '#001a4d', fontWeight: 500,
    padding: '12px 14px', background: '#f7f8fc',
    border: '1px solid #e4e8f0', borderRadius: '8px',
    marginBottom: '4px',
  },
  fieldInput: {
    width: '100%', padding: '12px 14px', fontSize: '14px',
    color: '#001a4d', background: 'white',
    border: '1.5px solid #c8d0e0', borderRadius: '8px',
    outline: 'none', fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute', right: '10px', top: '50%',
    transform: 'translateY(-50%)', background: 'none',
    border: 'none', cursor: 'pointer', padding: '4px',
  },
  errorBox: {
    background: '#fff5f5', border: '1.5px solid #fecaca',
    borderLeft: '4px solid #dc2626', borderRadius: '10px',
    padding: '12px 14px', marginBottom: '16px',
    display: 'flex', alignItems: 'flex-start', gap: '10px',
  },
  errorText: {
    fontSize: '13px', color: '#dc2626', lineHeight: 1.4,
  },
  submitButton: {
    width: '100%', height: '50px', border: 'none', borderRadius: '10px',
    fontFamily: "'Oswald', sans-serif", fontSize: '16px',
    fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    transition: 'all 0.15s',
  },
  linkButton: {
    width: '100%', marginTop: '14px', padding: '10px',
    background: 'none', border: '1px solid #c8d0e0', borderRadius: '8px',
    color: '#001a4d', fontFamily: "'Oswald', sans-serif", fontSize: '13px',
    fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase',
    cursor: 'pointer',
  },
};
